/**
 * Waitlist cache + button-state helpers.
 *
 * Tracks which variants the customer has registered for so the
 * "On waitlist" state can render without hitting the BIS API on every
 * page load. Cache is keyed by `customer.id` in localStorage so the
 * same browser shared across customers (e.g. household, CS reps) shows
 * the right state per login.
 *
 * Source path: theme/js/core/waitlist-cache.esm.js
 * Built name : ./waitlist-cache.js
 *
 * AI: Entirely written by AI
 * Date: 2026-05-06
 * Model: Claude Opus 4.7
 *
 * [WaitlistCache]
 */

import { log } from './log.js';
import { appGet } from './app-get.js';
import { customerId } from './customer.js';

const STORAGE_KEY = 'ep-waitlist_variants';

export function getWaitlistCache() {
  const id = customerId();
  if (!id) return [];
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    return data[id] || [];
  } catch (err) {
    return [];
  }
}

export function addToWaitlistCache(variantId) {
  const id = customerId();
  if (!id) return;
  try {
    const data = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    const cache = data[id] || [];
    if (cache.indexOf(variantId) === -1) {
      cache.push(variantId);
      data[id] = cache;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      log('[Waitlist] Cached variant=' + variantId + ' for customer=' + id);
    }
  } catch (err) {
    log('[Waitlist] localStorage unavailable', err);
  }
}

export function isVariantOnWaitlist(variantId) {
  return getWaitlistCache().indexOf(variantId) !== -1;
}

export function markWaitlistButton(button) {
  if (!button) return;
  const onWaitlistText = appGet('i18n.notifications.waitlist.on_waitlist');
  const label = button.querySelector('.js-waitlist-label');
  const iconDefault = button.querySelector('.js-waitlist-icon-default');
  const iconOnWaitlist = button.querySelector('.js-waitlist-icon-on-waitlist');
  if (label && onWaitlistText) label.textContent = onWaitlistText;
  if (iconDefault) iconDefault.setAttribute('hidden', '');
  if (iconOnWaitlist) iconOnWaitlist.removeAttribute('hidden');
  button.disabled = true;
  button.classList.add('on-waitlist');
  log('[Waitlist] Marked button as on-waitlist');
}

export function resetWaitlistButton(button) {
  if (!button || !button.classList.contains('on-waitlist')) return;
  const joinText = appGet('i18n.product.join_waitlist');
  const label = button.querySelector('.js-waitlist-label');
  const iconDefault = button.querySelector('.js-waitlist-icon-default');
  const iconOnWaitlist = button.querySelector('.js-waitlist-icon-on-waitlist');
  if (label && joinText) label.textContent = joinText;
  if (iconDefault) iconDefault.removeAttribute('hidden');
  if (iconOnWaitlist) iconOnWaitlist.setAttribute('hidden', '');
  button.disabled = false;
  button.classList.remove('on-waitlist');
  log('[Waitlist] Reset button to default state');
}

export const WAITLIST_STORAGE_KEY = 'ep-add_to_waitlist';
